import { useCallback, useEffect, useRef } from "react";

/**
 * Returns a stable debounced function. Rapid calls reset the timer so only
 * the last invocation runs. The latest callback is always used (no stale
 * closures). Pending work is cancelled on unmount and via `.cancel()`.
 */
export function useDebouncedCallback<T extends (...args: any[]) => void>(
  callback: T,
  delayMs: number,
) {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancel = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const debounced = useCallback(
    (...args: Parameters<T>) => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
      }

      if (delayMs <= 0) {
        callbackRef.current(...args);
        return;
      }

      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        callbackRef.current(...args);
      }, delayMs);
    },
    [delayMs],
  );

  useEffect(() => {
    return cancel;
  }, [cancel]);

  return Object.assign(debounced, { cancel });
}
